/**
 * Plane geometry for stand statistics. Areas are measured in a local equirectangular projection
 * around the data: at stand scale (a few km) its error is far below one percent.
 */
import type { FeatureCollection, Geometry, Position } from 'geojson';

const EARTH_RADIUS = 6371008.8;
const RAD = Math.PI / 180;

export type XY = [number, number];

export function localProjection(lon0: number, lat0: number): (p: Position) => XY {
  const k = RAD * EARTH_RADIUS;
  const cos = Math.cos(lat0 * RAD);
  return (p) => [(p[0]! - lon0) * k * cos, (p[1]! - lat0) * k];
}

export function polygonsOf(g: Geometry | null): Position[][][] {
  if (!g) return [];
  if (g.type === 'Polygon') return [g.coordinates];
  if (g.type === 'MultiPolygon') return g.coordinates;
  if (g.type === 'GeometryCollection') return g.geometries.flatMap(polygonsOf);
  return [];
}

/** A crown's representative point: the vertex mean of its outer ring, or the point itself. */
export function centroidOf(g: Geometry | null): XY | null {
  if (!g) return null;
  if (g.type === 'Point') return [g.coordinates[0]!, g.coordinates[1]!];
  const ring = polygonsOf(g)[0]?.[0];
  if (!ring?.length) return null;
  const closed = ring.length > 1 && ring[0]![0] === ring.at(-1)![0] && ring[0]![1] === ring.at(-1)![1];
  const pts = closed ? ring.slice(0, -1) : ring;
  return [pts.reduce((s, p) => s + p[0]!, 0) / pts.length, pts.reduce((s, p) => s + p[1]!, 0) / pts.length];
}

export function ringArea(ring: XY[]): number {
  let twice = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) twice += ring[j]![0] * ring[i]![1] - ring[i]![0] * ring[j]![1];
  return Math.abs(twice) / 2;
}

/** Andrew's monotone chain; counter-clockwise, without the closing point. */
export function convexHull(points: XY[]): XY[] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts;
  const cross = (o: XY, a: XY, b: XY) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (list: XY[]) => {
    const out: XY[] = [];
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2]!, out[out.length - 1]!, p) <= 0) out.pop();
      out.push(p);
    }
    out.pop();
    return out;
  };
  return [...half(pts), ...half([...pts].reverse())];
}

/**
 * Akl–Toussaint: the points not strictly inside the octagon of the eight extreme points (by x,
 * y, x + y and x − y). Only those can be on the hull, and on crown layers they are a sliver.
 */
export function hullCandidates(xs: ArrayLike<number>, ys: ArrayLike<number>): XY[] {
  const n = xs.length;
  if (!n) return [];
  // Counter-clockwise: E, NE, N, NW, W, SW, S, SE.
  const dirs: [number, number][] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
  const best = dirs.map(() => 0);
  const score = dirs.map(() => -Infinity);
  for (let i = 0; i < n; i++) {
    for (let d = 0; d < 8; d++) {
      const v = dirs[d]![0] * xs[i]! + dirs[d]![1] * ys[i]!;
      if (v > score[d]!) {
        score[d] = v;
        best[d] = i;
      }
    }
  }
  const same = (i: number, j: number) => xs[i] === xs[j] && ys[i] === ys[j];
  const poly = best.filter((idx, d) => !same(idx, best[(d + 7) % 8]!)).map((i): XY => [xs[i]!, ys[i]!]);
  const out: XY[] = [];
  for (let i = 0; i < n; i++) {
    const x = xs[i]!;
    const y = ys[i]!;
    let inside = poly.length >= 3;
    for (let k = 0; inside && k < poly.length; k++) {
      const [ax, ay] = poly[k]!;
      const [bx, by] = poly[(k + 1) % poly.length]!;
      if ((bx - ax) * (y - ay) - (by - ay) * (x - ax) <= 0) inside = false;
    }
    if (!inside) out.push([x, y]);
  }
  return out;
}

export interface Area {
  ha: number;
  /** `summary`: the forest summary's area of the whole (its plan's or its outline's). */
  source: 'boundary' | 'hull' | 'summary';
}

function centreOf(fc: FeatureCollection): XY | null {
  const cs = fc.features.map((f) => centroidOf(f.geometry)).filter((c): c is XY => c !== null);
  if (!cs.length) return null;
  return [cs.reduce((s, c) => s + c[0], 0) / cs.length, cs.reduce((s, c) => s + c[1], 0) / cs.length];
}

/**
 * The stand's area: the bound boundary's polygons, else the convex hull of every crown vertex —
 * precomputed as `hullHa` where the crowns themselves stayed in a worker and `trees` are their centroids.
 */
export function areaOf(trees: FeatureCollection, boundary?: FeatureCollection | null, hullHa?: number): Area {
  const centre = centreOf(trees) ?? (boundary ? centreOf(boundary) : null);
  if (!centre) return { ha: 0, source: 'hull' };
  const project = localProjection(centre[0], centre[1]);
  if (boundary?.features.length) {
    let m2 = 0;
    for (const poly of boundary.features.flatMap((f) => polygonsOf(f.geometry))) {
      const [outer, ...holes] = poly.map((ring) => ringArea(ring.map(project)));
      m2 += (outer ?? 0) - holes.reduce((s, h) => s + h, 0);
    }
    if (m2 > 0) return { ha: m2 / 10000, source: 'boundary' };
  }
  if (hullHa !== undefined) return { ha: hullHa, source: 'hull' };
  const xs: number[] = [];
  const ys: number[] = [];
  const add = (p: Position) => {
    const [x, y] = project(p);
    xs.push(x);
    ys.push(y);
  };
  for (const f of trees.features) {
    if (f.geometry?.type === 'Point') add(f.geometry.coordinates);
    for (const poly of polygonsOf(f.geometry)) for (const p of poly[0] ?? []) add(p);
  }
  const hull = convexHull(hullCandidates(xs, ys));
  return { ha: hull.length >= 3 ? ringArea(hull) / 10000 : 0, source: 'hull' };
}
